import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

const AllotmentFormSubrow = ({
  row,
  rowProps,
  visibleColumns,
  closeSubRow,
  companyId,
  onUpdate,
  onSubmit,
  mode,
  allocation,
}) => {
  const [showTargetsDropdown, setShowTargetsDropdown] = useState(false);

  // Get nominal shares to determine whether selected category exists, and to populate distribution targets
  const nominalShares = useSelector(
    (state) => state.companyDashboardSlice?.company?.nominalShares || []
  );

  // Get transactions for offer price lookup
  const transactions = useSelector(
    (state) =>
      state.initiatedTransactionsSlice?.initiatedTransactions?.items || []
  );

  // Get initial values from the allocation data
  const getInitialValues = () => {
    return {
      shareCategory: allocation.classOfShares || "",
      sharesOnOffer: allocation.sharesOnOffer || "",
      paidUpShares:
        allocation.AmountCreditedAsPaidUpCapitalPerShare ||
        allocation.paidUpShares ||
        "",
      forEvery: allocation.forEvery || "",
      allotAmount: allocation.allotAmount || "",
      distributionTargetClasses: allocation.distributionTargetClasses || [],
    };
  };

  // Helper function to get offer price per share for current allocation
  const getOfferPricePerShare = () => {
    try {
      // Try to get from allocation data
      if (allocation?.offerPricePerShare) {
        return Number(allocation.offerPricePerShare);
      }

      // Try to get from transactions if available
      if (transactions && transactions.length > 0) {
        const matchingTransaction = transactions.find((tx) =>
          tx?.shareCapitalChanges?.some(
            (change) =>
              change?.classOfShares === allocation?.classOfShares &&
              change?.sharesOnOffer === allocation?.sharesOnOffer
          )
        );

        if (matchingTransaction?.shareCapitalChanges) {
          const matchingChange = matchingTransaction.shareCapitalChanges.find(
            (change) =>
              change?.classOfShares === allocation?.classOfShares &&
              change?.sharesOnOffer === allocation?.sharesOnOffer
          );

          if (matchingChange?.offerPricePerShare) {
            return Number(matchingChange.offerPricePerShare);
          }
        }
      }

      return null;
    } catch (error) {
      console.error("Error getting offer price per share:", error);
      return null;
    }
  };

  const validationSchema = Yup.object().shape({
    shareCategory: Yup.string().required("Share category is required"),
    sharesOnOffer: Yup.number()
      .integer("Must be a whole number")
      .min(1, "Must be at least 1")
      .required("Shares on offer is required"),
    // Validate that paid-up amount does not exceed offer price per share
    paidUpShares: Yup.number()
      .required("Amount credited as paid-up per share is required")
      .test("not-greater-than-offer-price", function (value) {
        const offerPrice = getOfferPricePerShare();
        if (offerPrice === null || offerPrice === undefined) {
          // If we can't determine offer price, allow the validation to pass
          // This prevents blocking the form when offer price data is unavailable
          return true;
        }

        if (Number(value) > offerPrice) {
          return this.createError({
            message: `Amount credited as paid-up per share (${value}) cannot be greater than offer price per share (${offerPrice})`,
          });
        }

        return true;
      }),
    // If selected share category has sharesNumber === 0 (or is missing), require at least one target class
    distributionTargetClasses: Yup.array().test(
      "require-when-nonexistent",
      "Select at least one target class to base distribution on",
      function (val) {
        const sc = this.parent.shareCategory;
        const n = nominalShares.find((ns) => ns?.sharesCategory === sc);
        const sharesNum = Number(n?.sharesNumber || 0);
        const nonExistent = !n || sharesNum === 0;
        if (!nonExistent) return true;
        return Array.isArray(val) && val.length >= 1;
      }
    ),
    // Rights Issue specific fields
    ...(mode === "rightsIssue" && {
      forEvery: Yup.number()
        .integer("Must be a whole number")
        .min(1, "Must be at least 1")
        .required("For every is required"),
      allotAmount: Yup.number()
        .min(0.01, "Must be greater than 0")
        .required("Allot amount is required"),
    }),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      // Create payload for the allocation calculation (same format as main form)
      const payload = {
        option: mode,
        shareCategory: values.shareCategory,
        sharesOnOffer: Number(values.sharesOnOffer),
        paidUpShares: Number(values.paidUpShares),
        distributionTargetClasses: values.distributionTargetClasses || [],
        ...(mode === "rightsIssue" && {
          forEvery: Number(values.forEvery),
          allotAmount: Number(values.allotAmount),
        }),
      };

      // Perform the actual rights issue or pro rata calculation
      if (onSubmit) {
        const result = await onSubmit(payload, {
          setSubmitting: () => {}, // Pass dummy function since we handle it locally
          setSubmittedPayload: () => {}, // Pass dummy function
        });

        // Update the allocation with the calculated result
        if (result && onUpdate) {
          await onUpdate(allocation._index, {
            ...result,
            _index: allocation._index, // Preserve the index for table functionality
          });
        }
      } else if (onUpdate) {
        // Fallback: just update the display values if onSubmit is not available
        const updatedAllocation = {
          ...allocation,
          classOfShares: values.shareCategory,
          sharesOnOffer: Number(values.sharesOnOffer),
          AmountCreditedAsPaidUpCapitalPerShare: Number(values.paidUpShares),
          paidUpShares: Number(values.paidUpShares),
          forEvery:
            mode === "rightsIssue"
              ? Number(values.forEvery)
              : allocation.forEvery,
          allotAmount:
            mode === "rightsIssue"
              ? Number(values.allotAmount)
              : allocation.allotAmount,
          distributionTargetClasses: values.distributionTargetClasses,
          _index: allocation._index,
        };

        await onUpdate(allocation._index, updatedAllocation);
      }

      closeSubRow();
    } catch (error) {
      console.error("Error updating allocation:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <tr>
      <td colSpan={visibleColumns.length} className="p-0">
        <div className="bg-gray-50 p-6 border-t">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Edit {mode === "rightsIssue" ? "Rights Issue" : "Pro Rata"}{" "}
              Allocation
            </h3>

            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ isSubmitting, values, setFieldValue }) => {
                // Get the current offer price for display
                const currentOfferPrice = getOfferPricePerShare();

                return (
                  <Form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Share Category - Disabled */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Share Category
                        </label>
                        <Field
                          type="text"
                          name="shareCategory"
                          disabled
                          className="w-full p-2 border border-gray-300 bg-gray-200 rounded"
                        />
                        <ErrorMessage
                          name="shareCategory"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Shares on Offer - Disabled */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Shares on Offer
                        </label>
                        <Field
                          type="number"
                          name="sharesOnOffer"
                          disabled
                          className="w-full p-2 border border-gray-300 bg-gray-200 rounded"
                        />
                        <ErrorMessage
                          name="sharesOnOffer"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Paid-up per Share - Editable */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount credited as paid-up per share
                        </label>
                        <Field
                          type="number"
                          name="paidUpShares"
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        {currentOfferPrice !== null && (
                          <div className="text-xs text-gray-500 mt-1">
                            Maximum allowed: {currentOfferPrice} (Offer price
                            per share)
                          </div>
                        )}
                        <ErrorMessage
                          name="paidUpShares"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Rights Issue specific fields */}
                      {mode === "rightsIssue" && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              For Every
                            </label>
                            <Field
                              type="number"
                              name="forEvery"
                              className="w-full p-2 border border-gray-300 rounded"
                            />
                            <ErrorMessage
                              name="forEvery"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Allot Amount
                            </label>
                            <Field
                              type="number"
                              name="allotAmount"
                              className="w-full p-2 border border-gray-300 rounded"
                            />
                            <ErrorMessage
                              name="allotAmount"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Distribution Target Classes - Conditional */}
                    {(() => {
                      const sel = nominalShares.find(
                        (n) => n?.sharesCategory === values.shareCategory
                      );
                      const nonExistent =
                        !sel || Number(sel?.sharesNumber || 0) === 0;
                      if (!nonExistent) return null;
                      return (
                        <div className="col-span-full">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Distribute to holders of classes
                          </label>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setShowTargetsDropdown((prev) => !prev)
                              }
                              className="w-full flex justify-between items-center p-2 border border-gray-300 rounded"
                            >
                              <span className="text-gray-700">
                                {(values.distributionTargetClasses || [])
                                  .length > 0 ? (
                                  <span className="flex text-xs flex-wrap gap-1">
                                    {(
                                      values.distributionTargetClasses || []
                                    ).map((cls) => (
                                      <span
                                        key={cls}
                                        className="inline-flex items-start px-2 py-1 rounded bg-sflPrimary text-white text-xs"
                                      >
                                        {cls}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  "Select Distribution Class(es)"
                                )}
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 transform transition-transform ${
                                  showTargetsDropdown
                                    ? "rotate-180"
                                    : "rotate-0"
                                }`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                            {showTargetsDropdown && (
                              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow max-h-60 overflow-auto">
                                <ul className="py-1 text-sm text-gray-700">
                                  {nominalShares
                                    .filter(
                                      (n) =>
                                        n?.sharesCategory !==
                                        values.shareCategory
                                    )
                                    .map((n) => {
                                      const value = n.sharesCategory;
                                      const checked = (
                                        values.distributionTargetClasses || []
                                      ).includes(value);
                                      return (
                                        <li
                                          key={n._id || value}
                                          className="px-2 py-1 hover:bg-gray-50"
                                        >
                                          <label className="inline-flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              className="h-4 w-4"
                                              checked={checked}
                                              onChange={(e) => {
                                                const prev =
                                                  values.distributionTargetClasses ||
                                                  [];
                                                const next = e.target.checked
                                                  ? Array.from(
                                                      new Set([...prev, value])
                                                    )
                                                  : prev.filter(
                                                      (v) => v !== value
                                                    );
                                                setFieldValue(
                                                  "distributionTargetClasses",
                                                  next
                                                );
                                              }}
                                            />
                                            <span>{value}</span>
                                          </label>
                                        </li>
                                      );
                                    })}
                                </ul>
                              </div>
                            )}
                          </div>
                          <ErrorMessage
                            name="distributionTargetClasses"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      );
                    })()}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={closeSubRow}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-sflPrimary border border-transparent rounded-md hover:bg-sflPrimary-dark disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <SpinnerIcon className="mr-2 h-4 w-4" />
                            Updating...
                          </>
                        ) : (
                          "Update Allocation"
                        )}
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default AllotmentFormSubrow;
